import React from 'react';
import { connect } from 'react-redux';
import T from 'components/T';
import { ReportStatuses } from '@dashboard-experience/utils';

import { useCheckrPersonalBannerState } from 'components/CheckrPersonalProfileBanner/useCheckrPersonalBannerState';
import styles from './ReviewCTABanner.module.scss';

// Marketing-owned Qualtrics survey for collecting candidate reviews; sourced
// from Jack Huettel's scope-for-CDX comment on NPI-2573 (linked from PCDX-251).
const QUALTRICS_SURVEY_URL =
  'https://checkr.sjc1.qualtrics.com/jfe/form/SV_3CO3QsLSUkBTJnU';

interface ReviewCTABannerReport {
  status?: string;
  [key: string]: unknown;
}

interface ReviewCTABannerProps {
  reports?: ReviewCTABannerReport[];
}

const isAllReportsClear = (reports?: ReviewCTABannerReport[]): boolean => {
  if (!reports || reports.length === 0) return false;
  return reports.every(
    report => report?.status?.toLowerCase() === ReportStatuses.CLEAR,
  );
};

export const ReviewCTABanner: React.FC<ReviewCTABannerProps> = ({
  reports,
}) => {
  // Per PCDX-251 / NPI-2573: only display the Review CTA when the
  // LegacyPersonalProfileBanner ("Profiles" CTA) is not already rendering, so
  // the two banners never compete for the same header real estate. Defer until
  // the Profiles Harness flag has resolved so we don't flash this banner
  // during the initial fetch and then unmount it when the legacy banner takes
  // over. The new CheckrPersonalProfileBanner (treatment `on`) lives in main
  // content and can coexist with this banner.
  const { isVisible: legacyPersonalProfileBannerVisible, isFlagLoaded } =
    useCheckrPersonalBannerState();

  if (!isFlagLoaded) return null;
  if (legacyPersonalProfileBannerVisible) return null;
  if (!isAllReportsClear(reports)) return null;

  return (
    <div className={styles.reviewCtaBanner} data-testid='review-cta-banner'>
      <div className={styles.bannerVariantContent}>
        <h4 className={styles.header}>
          <T value='components.ReviewCTABanner.message' />
        </h4>
        <a
          className={styles.claimButtonVariant}
          href={QUALTRICS_SURVEY_URL}
          target='_blank'
          rel='noopener noreferrer'
        >
          <T value='components.ReviewCTABanner.cta' />
        </a>
      </div>
    </div>
  );
};

const mapStateToProps = (state: {
  portal?: { reports?: ReviewCTABannerReport[] };
}) => ({
  reports: state.portal?.reports,
});

export default connect(mapStateToProps)(ReviewCTABanner);
